/**
 * User-facing strings for the admin deletion-requests screen.
 *
 * Extracted from the dialog so the en/ar pair can be parsed and key-compared by
 * a test. A missing Arabic key is invisible at runtime in this app — the
 * component indexes the table directly, so a gap renders `undefined` rather
 * than falling back — which is exactly the sort of thing that only shows up in
 * front of an Arabic-speaking admin holding a destructive button.
 *
 * Western digits only, in both languages.
 */

export const deletionStrings = {
  en: {
    title: 'Account Deletion Requests',
    empty: 'No deletion requests',
    emptyDesc: 'There are no account deletion requests at this time.',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    approve: 'Review & Delete',
    reject: 'Reject',
    phone: 'Phone',
    email: 'Email',
    reason: 'Reason',
    noReason: 'No reason provided',
    noEmail: 'Not provided',
    submitted: 'Submitted',
    processedBy: 'Processed by',
    confirmReject: 'Are you sure you want to reject this deletion request?',
    processing: 'Processing...',
    all: 'All',
    deleted: 'Account deleted. Clinical examination records were retained.',
    alreadyDeleted: 'This account was already deleted. Nothing further to do.',
    failedTitle: 'Nothing was deleted',
    partialTitle: 'Deletion may be incomplete',
    errNoMatch: 'No user matches this phone number, so nothing was deleted. Check the number, or reject the request.',
    errAmbiguous: 'Several users share this phone number. The account was NOT deleted — resolve the duplicate users first, then try again.',
    errConflict: 'The user recorded on this request does not match the user this phone number points at. The account was NOT deleted.',
    errUnverified: 'This request names a user that could not be verified against the phone number on it. Nothing was deleted. Treat the request as untrusted.',
    errTargetAdmin: 'That account holds an admin role and cannot be deleted through this screen. Nothing was deleted.',
    errPermission: 'You do not have permission to delete accounts. An admin role is required.',
    errIncomplete: 'The deletion stopped part-way, so some data may already be gone while the account still exists. The request stays pending — approve it again to resume.',
    errTimeout: 'The deletion is taking longer than this screen will wait. It may still be running in the background. Reopen this dialog in a few minutes; if the request is still pending, approve it again to resume.',
    errRejected: 'This request was already rejected.',
    errReject: 'Could not reject the request. Nothing was changed.',
    errGeneric: 'The deletion failed. Some data may already have been deleted — approve the request again to resume.',
    errConfirmRequired: 'No account was confirmed, so nothing was deleted. Open the request again and confirm the account shown.',
    errConfirmMismatch: 'The account you confirmed is not the account this request resolves to. Nothing was deleted. Reopen the request and check it again.',
    retainedNote: 'Clinical examination records (clinicalExams, examinations) are retained by design.',

    // --- the confirmation step ---
    confirmTitle: 'Confirm the account to delete',
    resolving: 'Looking up the account this request points at...',
    aboutToDelete: 'You are about to permanently delete this account:',
    accountName: 'Name',
    accountEmail: 'Email on the account',
    accountPhone: 'Phone on the account',
    accountJoined: 'Joined',
    accountUid: 'Account ID',
    noName: 'This account has no name recorded',
    joinedUnknown: 'Unknown',
    typedNotice: 'The phone, email and reason on the request are typed into a public form by whoever submitted it. The details above were read from the account itself — approve only if they are the person who should be deleted.',
    ownershipUnprovenTitle: 'Phone ownership was NOT verified',
    ownershipUnproven: 'The public request form needs no sign-in and no one-time code, so nothing proves that the person who typed this number owns it. Confirm the account another way before approving.',
    ownershipClaimed: 'The request claims the number was verified, but that claim was written by the requester and the server could not confirm it.',
    ownershipProven: 'The server verified phone ownership for this request. Method:',
    confirmCheckbox: 'I have checked this is the correct account.',
    confirmButton: 'Permanently delete this account',
    cancel: 'Cancel',
    resolveNoMatch: 'This phone number does not match any account, so there is nothing to delete. Reject the request instead.',
    resolveAmbiguous: 'This phone number matches more than one account. Deleting would be a guess — fix the duplicate accounts first.',
    resolveFailed: 'The account could not be looked up, so there is nothing safe to confirm. Try again in a moment.',
    resolveIsAdmin: 'This phone number belongs to an account holding an admin role. It cannot be deleted from this screen.',
    resumeTitle: 'Resuming an unfinished deletion',
    resumeNote: 'No account matches this number any more, and this request records an earlier run against the account below. Confirming will resume that deletion. Nothing happens unless the server can prove it started that run itself.',
  },
  ar: {
    title: 'طلبات حذف الحسابات',
    empty: 'لا توجد طلبات حذف',
    emptyDesc: 'لا توجد طلبات حذف حسابات في الوقت الحالي.',
    pending: 'قيد المراجعة',
    approved: 'تمت الموافقة',
    rejected: 'مرفوض',
    approve: 'مراجعة وحذف',
    reject: 'رفض',
    phone: 'الجوال',
    email: 'البريد',
    reason: 'السبب',
    noReason: 'لم يتم تقديم سبب',
    noEmail: 'غير متوفر',
    submitted: 'تاريخ الطلب',
    processedBy: 'تمت المعالجة بواسطة',
    confirmReject: 'هل أنت متأكد من رفض طلب الحذف هذا؟',
    processing: 'جاري المعالجة...',
    all: 'الكل',
    deleted: 'تم حذف الحساب. تم الاحتفاظ بسجلات الفحوصات السريرية.',
    alreadyDeleted: 'تم حذف هذا الحساب مسبقًا. لا يوجد إجراء إضافي.',
    failedTitle: 'لم يتم حذف أي شيء',
    partialTitle: 'قد يكون الحذف غير مكتمل',
    errNoMatch: 'لا يوجد مستخدم مطابق لرقم الجوال هذا، لذلك لم يتم حذف أي شيء. تحقق من الرقم أو ارفض الطلب.',
    errAmbiguous: 'يوجد أكثر من مستخدم بنفس رقم الجوال. لم يتم حذف الحساب — عالج تكرار المستخدمين أولًا ثم أعد المحاولة.',
    errConflict: 'المستخدم المسجَّل في هذا الطلب لا يطابق المستخدم الذي يشير إليه رقم الجوال. لم يتم حذف الحساب.',
    errUnverified: 'هذا الطلب يشير إلى مستخدم تعذّر التحقق منه مقابل رقم الجوال المذكور فيه. لم يتم حذف أي شيء. تعامل مع الطلب على أنه غير موثوق.',
    errTargetAdmin: 'هذا الحساب يحمل دور مسؤول (admin) ولا يمكن حذفه من هذه الشاشة. لم يتم حذف أي شيء.',
    errPermission: 'لا تملك صلاحية حذف الحسابات. يلزم دور مسؤول (admin).',
    errIncomplete: 'توقّف الحذف قبل اكتماله، لذلك قد تكون بعض البيانات حُذفت بينما لا يزال الحساب موجودًا. يبقى الطلب قيد المراجعة — وافق عليه مرة أخرى لاستئناف العملية.',
    errTimeout: 'تستغرق عملية الحذف وقتًا أطول مما تنتظره هذه الشاشة، وقد تكون لا تزال تعمل في الخلفية. أعد فتح هذه النافذة بعد بضع دقائق، وإذا بقي الطلب قيد المراجعة فوافق عليه مرة أخرى لاستئناف العملية.',
    errRejected: 'تم رفض هذا الطلب مسبقًا.',
    errReject: 'تعذّر رفض الطلب. لم يتم تغيير أي شيء.',
    errGeneric: 'فشلت عملية الحذف. قد تكون بعض البيانات حُذفت بالفعل — وافق على الطلب مرة أخرى لاستئناف العملية.',
    errConfirmRequired: 'لم يتم تأكيد أي حساب، لذلك لم يُحذف أي شيء. افتح الطلب مرة أخرى وأكّد الحساب المعروض.',
    errConfirmMismatch: 'الحساب الذي أكّدته ليس الحساب الذي يشير إليه هذا الطلب. لم يتم حذف أي شيء. أعد فتح الطلب وتحقق منه مرة أخرى.',
    retainedNote: 'يتم الاحتفاظ بسجلات الفحوصات السريرية (clinicalExams، examinations) عمدًا.',

    // --- خطوة التأكيد ---
    confirmTitle: 'أكّد الحساب المراد حذفه',
    resolving: 'جاري البحث عن الحساب الذي يشير إليه هذا الطلب...',
    aboutToDelete: 'أنت على وشك حذف هذا الحساب نهائيًا:',
    accountName: 'الاسم',
    accountEmail: 'البريد المسجَّل في الحساب',
    accountPhone: 'الجوال المسجَّل في الحساب',
    accountJoined: 'تاريخ الانضمام',
    accountUid: 'معرّف الحساب',
    noName: 'لا يوجد اسم مسجَّل على هذا الحساب',
    joinedUnknown: 'غير معروف',
    typedNotice: 'الجوال والبريد والسبب في الطلب يكتبها من قدّم الطلب في نموذج عام. أما التفاصيل أعلاه فقد قُرئت من الحساب نفسه — لا توافق إلا إذا كان هذا هو الشخص المطلوب حذفه.',
    ownershipUnprovenTitle: 'لم يتم التحقق من ملكية رقم الجوال',
    ownershipUnproven: 'نموذج الطلب العام لا يتطلب تسجيل دخول ولا رمز تحقق، لذلك لا يوجد ما يثبت أن من كتب هذا الرقم يملكه فعلًا. تأكّد من الحساب بطريقة أخرى قبل الموافقة.',
    ownershipClaimed: 'يدّعي الطلب أنه تم التحقق من الرقم، لكن هذا الادعاء كتبه مقدّم الطلب ولم يتمكن الخادم من تأكيده.',
    ownershipProven: 'تحقّق الخادم من ملكية رقم الجوال لهذا الطلب. الطريقة:',
    confirmCheckbox: 'لقد تحققت من أن هذا هو الحساب الصحيح.',
    confirmButton: 'احذف هذا الحساب نهائيًا',
    cancel: 'إلغاء',
    resolveNoMatch: 'رقم الجوال هذا لا يطابق أي حساب، فلا يوجد ما يُحذف. ارفض الطلب بدلًا من ذلك.',
    resolveAmbiguous: 'رقم الجوال هذا يطابق أكثر من حساب. الحذف سيكون تخمينًا — عالج تكرار الحسابات أولًا.',
    resolveFailed: 'تعذّر البحث عن الحساب، فلا يوجد ما يمكن تأكيده بأمان. أعد المحاولة بعد قليل.',
    resolveIsAdmin: 'رقم الجوال هذا يخص حسابًا يحمل دور مسؤول (admin)، ولا يمكن حذفه من هذه الشاشة.',
    resumeTitle: 'استئناف عملية حذف غير مكتملة',
    resumeNote: 'لم يعد أي حساب يطابق هذا الرقم، ويسجّل هذا الطلب محاولة سابقة على الحساب أدناه. التأكيد سيستأنف ذلك الحذف. ولن يحدث شيء ما لم يستطع الخادم إثبات أنه هو من بدأ تلك المحاولة.',
  },
} as const;

export type DeletionLanguage = keyof typeof deletionStrings;
export type DeletionStringKey = keyof (typeof deletionStrings)['en'];
